import React from 'react'

import Link from 'next/link'
import { HiMiniXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Thumbnail,
  ThumbnailContainer,
  type ThumbnailType,
} from '@/components/common/Attachment/Thumbnail'
import Spinner from '@/components/common/Spinner'

import type { File as UploadedFile } from '@/types/File'

export interface FileAttachment {
  type: 'file'
  file: File | UploadedFile
}

export type Attachment = {
  label: string
  description: string
  thumbnail: ThumbnailType | string
  isLoading: boolean
  href?: string
  className?: string
  onDelete?: () => void
}

export const AttachmentBase: React.FC<Attachment> = ({
  label,
  description,
  thumbnail,
  isLoading,
  className,
}) => {
  return (
    <div
      className={twMerge('flex items-center gap-x-[10px] w-full', className)}
    >
      {isLoading ? (
        <ThumbnailContainer className="p-[6px] flex-shrink-0 bg-priamry-200 dark:bg-primary-700">
          <Spinner className="w-4 h-4 fill-primary-700 dark:fill-primary-300 text-primary-200 dark:text-primary-700" />
        </ThumbnailContainer>
      ) : (
        <Thumbnail type={thumbnail} className="p-[6px] flex-shrink-0" />
      )}
      <div className="flex flex-col text-xs w-full min-w-0">
        <span className="font-medium text-primary-900 dark:text-primary-100 truncate">
          {label}
        </span>
        <span className="text-primary-500 dark:text-primary-400 truncate">
          {description}
        </span>
      </div>
    </div>
  )
}

const AttachmentBaseWithDelete: React.FC<Attachment> = ({
  onDelete,
  ...props
}) => {
  return (
    <>
      <AttachmentBase {...props} />
      {onDelete && !props.isLoading && (
        <button
          className="group-hover:opacity-100 opacity-0 hover:opacity-70 absolute -top-2 -right-2 p-[1px] rounded-full border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-800"
          onClick={onDelete}
        >
          <HiMiniXMark className="w-[13px] h-[13px] text-primary-500 dark:text-primary-400" />
        </button>
      )}
    </>
  )
}

const Attachment: React.FC<Attachment> = ({ className, href, ...props }) => {
  const commonClassName = twMerge(
    'relative group flex items-center gap-x-2 rounded-md transition-colors duration-100 border border-primary-200 dark:border-primary-700 px-[6px] py-1 bg-primary-50 dark:bg-primary-800',
    href === undefined && 'cursor-default',
    className,
  )

  return (
    // if there is onDelete, it is not a link
    href && props.onDelete === undefined ? (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={twMerge(
          commonClassName,
          'hover:bg-primary-100 dark:hover:bg-primary-700',
        )}
      >
        <AttachmentBase {...props} />
      </Link>
    ) : (
      <div className={commonClassName}>
        <AttachmentBaseWithDelete {...props} />
      </div>
    )
  )
}

export default Attachment
